import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ImageUp, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { compressImage } from "../lib/image";
import { useAuth } from "../context/AuthContext";
import { stateName } from "../constants/states";
import { RegionSelect } from "../components/RegionSelect";
import { KeeperBadge, EdibleBadge, KEEPER_DISCLAIMER } from "../components/Badges";
import { CatchForm } from "../components/CatchForm";

function RegionGate() {
  const { setUser } = useAuth();
  const [region, setRegion] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/profile", { region });
      setUser(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="brut-card-hot p-6 space-y-5 rise" data-testid="region-gate">
      <h2 className="font-display text-2xl uppercase">Where do you fish?</h2>
      <p className="text-sm text-zinc-400">Pick your state so we can check keeper sizes. You can change it later in Profile.</p>
      <RegionSelect value={region} onChange={setRegion} testId="region-gate-select" />
      <button className="brut-btn w-full" disabled={!region || busy} onClick={save} data-testid="region-gate-save-btn">
        {busy ? "Saving…" : "Save region"}
      </button>
    </div>
  );
}

function ResultCard({ r, region }) {
  return (
    <div className="brut-card-hot p-5 space-y-4 rise" data-testid="scan-result-card">
      <div>
        <span className="brut-label !mb-1">Species · {r.confidence || "?"} confidence</span>
        <h2 className="font-display text-3xl uppercase leading-none" data-testid="scan-result-species">{r.species}</h2>
        {r.scientific_name && <p className="text-sm italic text-zinc-400 mt-1">{r.scientific_name}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <EdibleBadge edible={r.edible} testId="scan-result-edible" />
        <KeeperBadge status={r.keeper_status} testId="scan-result-keeper" />
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        {[["Est. length", r.length_in != null ? `${r.length_in}"` : "–", "scan-result-length"],
          ["Min size", r.min_size_in != null ? `${r.min_size_in}"` : "–", "scan-result-min"],
          ["Bag limit", r.bag_limit || "–", "scan-result-bag"]].map(([l, v, id]) => (
          <div key={l} className="bg-[#1E1E24] border-2 border-[#27272A] p-3" data-testid={id}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{l}</div>
            <div className="font-display text-lg mt-1 break-words">{v}</div>
          </div>
        ))}
      </div>
      {r.edible_note && <p className="text-sm text-zinc-300">{r.edible_note}</p>}
      {r.keeper_note && <p className="text-sm text-zinc-300"><b className="text-white">{stateName(region)}:</b> {r.keeper_note}</p>}
      <p className="flex gap-2 text-xs text-[#EAB308] border-2 border-[#EAB308]/40 p-3" data-testid="scan-keeper-disclaimer">
        <Info size={16} className="shrink-0" /> {KEEPER_DISCLAIMER}
      </p>
    </div>
  );
}

export default function Scan() {
  const { user } = useAuth();
  const nav = useNavigate();
  const cameraRef = useRef();
  const uploadRef = useRef();
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [spots, setSpots] = useState([]);

  useEffect(() => { api.get("/spots").then((r) => setSpots(r.data)).catch(() => {}); }, []);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const onFile = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = "";
    if (!raw) return;
    const file = await compressImage(raw);
    setPreview(URL.createObjectURL(file));
    setScan(null);
    setShowForm(false);
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/scan", fd);
      setScan(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setScanning(false);
    }
  };

  const save = async (form) => {
    setSaving(true);
    const r = scan.result;
    try {
      const { data } = await api.post("/catches", {
        ...form, photo_path: scan.photo_path, scientific_name: r.scientific_name, edible: r.edible,
        edible_note: r.edible_note, keeper_status: r.keeper_status, keeper_note: r.keeper_note,
        min_size_in: r.min_size_in, max_size_in: r.max_size_in, bag_limit: r.bag_limit,
      });
      toast.success("Catch saved to your log");
      nav(`/log/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => { setPreview(null); setScan(null); setShowForm(false); };

  if (!user.region) return <RegionGate />;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none">Scan<br /><span className="text-[#FF4D00]">a catch</span></h1>
        <span className="text-xs text-zinc-400 text-right">Rules for<br /><b className="text-white">{stateName(user.region)}</b></span>
      </div>

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} data-testid="scan-camera-input" />
      <input ref={uploadRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onFile} data-testid="scan-upload-input" />

      <div className="relative aspect-[4/3] bg-[#141417] border-2 border-[#27272A] overflow-hidden" data-testid="scan-viewfinder">
        {preview ? (
          <img src={preview} alt="Your catch" className="w-full h-full object-cover" data-testid="scan-preview-img" />
        ) : (
          <button onClick={() => cameraRef.current.click()} className="w-full h-full flex flex-col items-center justify-center gap-3 text-zinc-500 hover:text-white transition-colors" data-testid="scan-viewfinder-btn">
            <Camera size={56} strokeWidth={1.5} />
            <span className="text-xs font-bold uppercase tracking-widest">Tap to take a photo</span>
          </button>
        )}
        {["top-3 left-3 border-t-4 border-l-4", "top-3 right-3 border-t-4 border-r-4", "bottom-3 left-3 border-b-4 border-l-4", "bottom-3 right-3 border-b-4 border-r-4"].map((c) => (
          <span key={c} className={`absolute w-8 h-8 border-[#FF4D00] pointer-events-none ${c}`} />
        ))}
        {scanning && (
          <div className="absolute inset-0 bg-black/40" data-testid="scan-loading">
            <div className="scan-line" />
            <div className="absolute bottom-4 inset-x-0 text-center font-display uppercase text-[#FF4D00]">Identifying…</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <button className="brut-btn" onClick={() => cameraRef.current.click()} disabled={scanning} data-testid="scan-take-photo-btn">
          <Camera size={18} /> Camera
        </button>
        <button className="brut-btn-ghost" onClick={() => uploadRef.current.click()} disabled={scanning} data-testid="scan-upload-btn">
          <ImageUp size={18} /> Upload
        </button>
      </div>

      {scan && <ResultCard r={scan.result} region={scan.region} />}

      {scan && scan.result.is_fish !== false && !showForm && (
        <div className="grid grid-cols-[1fr_auto] gap-4">
          <button className="brut-btn" onClick={() => setShowForm(true)} data-testid="scan-save-to-log-btn">Save to catch log</button>
          <button className="brut-btn-ghost !px-4" onClick={reset} aria-label="Scan again" data-testid="scan-reset-btn"><RotateCcw size={18} /></button>
        </div>
      )}

      {showForm && (
        <div className="brut-card p-5 rise">
          <h2 className="brut-h2 mb-6">Log it</h2>
          <CatchForm initial={{ species: scan.result.species, length_in: scan.result.length_in }} spots={spots}
            onSubmit={save} busy={saving} submitLabel="Save catch" />
        </div>
      )}
    </div>
  );
}
